import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useMatch } from 'react-router-dom';

import { Spinner } from '@/components/atoms/Spinner';
import PageLayout from '@/components/Layout/PageLayout';
import { NotFound } from '@/components/molecules/NotFound';
import Tabs from '@/components/molecules/Tabs';
import { useCage, useCageName } from '@/contexts/cage';
import { useCageScopedSearch } from '@/contexts/cage-filter-context';
import { useIsLandbased, useIsSmolt } from '@/hooks/useCageTypeFlags';

import { usePenUrl } from '../../usePenUrl';

import HarvestReportDetailContainer from './HarvestReport/HarvestReportDetailContainer';
import HarvestReportPageContainer from './HarvestReport/HarvestReportPageContainer';
import SmoltReportDetailContainer from './SmoltReport/SmoltReportDetailContainer';
import SmoltReportPageContainer from './SmoltReport/SmoltReportPageContainer';
import PageSubtitleRow from './shared/PageSubtitleRow';

export default function ReportsLayout() {
    const { cageId, loading: cageLoading } = useCage();
    const cageName = useCageName();
    const isSmolt = useIsSmolt();
    const isLandbased = useIsLandbased();
    const { t } = useTranslation();

    const reportsUrl = `${usePenUrl()}/reports`;
    const harvestListPath = `${reportsUrl}/harvest`;
    const smoltListPath = `${reportsUrl}/smolt`;
    // Tabs and redirects keep the cage-scoped params, so the list keeps the fish group.
    const search = useCageScopedSearch();

    // Match like the <Route>s below (whole path), so a trailing slash still counts.
    const isHarvestList = useMatch(harvestListPath) !== null;
    const isSmoltList = useMatch(smoltListPath) !== null;
    const isListRoute = isHarvestList || isSmoltList;

    // Route guards: a pen only has the report type its cage type allows.
    const toSmolt =
        !cageLoading && isSmolt ? <Navigate to={`${smoltListPath}${search}`} replace /> : undefined;
    const toHarvest =
        !cageLoading && !isSmolt && !isLandbased ? (
            <Navigate to={`${harvestListPath}${search}`} replace />
        ) : undefined;

    // Detail routes render their own PageLayout, so only the list routes get one here. The
    // layout stays mounted while the cage loads so the list page isn't remounted afterwards.
    const routes = (
        <Routes>
            <Route
                path="harvest"
                element={toSmolt ?? <HarvestReportPageContainer cageId={cageId} />}
            />
            <Route
                path="harvest/:reportId"
                element={toSmolt ?? <HarvestReportDetailContainer cageId={cageId} />}
            />
            <Route
                path="smolt"
                element={toHarvest ?? <SmoltReportPageContainer cageId={cageId} />}
            />
            <Route
                path="smolt/:reportId"
                element={toHarvest ?? <SmoltReportDetailContainer cageId={cageId} />}
            />
            <Route
                index
                element={
                    cageLoading ? (
                        <Spinner size="lg" fullScreen />
                    ) : (
                        <Navigate
                            to={`${isSmolt ? smoltListPath : harvestListPath}${search}`}
                            replace
                        />
                    )
                }
            />
            <Route path="*" element={<NotFound />} />
        </Routes>
    );

    if (!isListRoute) {
        return routes;
    }

    return (
        <PageLayout title={cageName} subtitle={t('reports.titles.mainPage')}>
            {cageLoading ? null : isLandbased ? (
                <Tabs
                    value={isHarvestList ? 'harvest' : isSmoltList ? 'smolt' : undefined}
                    items={[
                        {
                            value: 'harvest',
                            label: t('reports.titles.harvestPage'),
                            to: `${harvestListPath}${search}`,
                        },
                        {
                            value: 'smolt',
                            label: t('reports.titles.smoltPage'),
                            to: `${smoltListPath}${search}`,
                        },
                    ]}
                />
            ) : (
                <PageSubtitleRow
                    subtitle={t(
                        isSmolt ? 'reports.titles.smoltPage' : 'reports.titles.harvestPage'
                    )}
                />
            )}
            {routes}
        </PageLayout>
    );
}
